/**
 * @file: inventoryCache.web.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  asyncStorageSubjectForKey$,
  storageKeyWrapped,
} from '@euler/functions/useAsyncStorage';
import { makeDebug } from '@euler/utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { IDBPDatabase, openDB } from 'idb';
import { BehaviorSubject, concatMap, from } from 'rxjs';

const debug = makeDebug('inventory-cache');

const kDatabaseName = 'euler.storage';
const kDatabaseVersion = 1;
const kObjectStoreName = 'bulk-items';

const isIndexedDbAvailable =
  typeof indexedDB !== 'undefined' && indexedDB != null;

let database: Promise<IDBPDatabase> | undefined = undefined;

const getDatabase = () => {
  if (!database) {
    database = openDB(kDatabaseName, kDatabaseVersion, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(kObjectStoreName)) {
          db.createObjectStore(kObjectStoreName);
        }
      },
    }).catch(e => {
      // let the next call retry opening the database.
      database = undefined;
      throw e;
    });
  }
  return database;
};

const cache = new Map<string, BehaviorSubject<any>>();

/**
 * the inventory data blows the local storage quota the async storage is backed
 * by on web (~5mb per origin), so it is kept in indexeddb instead. the subject
 * keeps the contract of `asyncStorageSubjectForKey$`: it starts `undefined`,
 * emits the stored value (or `null` when there is none) once loaded, and
 * persists whatever is pushed into it.
 */
export const inventoryCacheSubjectForKey$ = <T>(
  key: string,
): BehaviorSubject<T | undefined | null> => {
  if (!isIndexedDbAvailable) {
    console.error(
      'indexeddb is unavailable, the inventory data falls back to the (size limited) async storage',
    );
    return asyncStorageSubjectForKey$<T>(key);
  }

  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const subject$ = new BehaviorSubject<T | undefined | null>(undefined);
  cache.set(key, subject$);

  const storageKey = storageKeyWrapped(key);

  const read = async (): Promise<string | null> => {
    const db = await getDatabase();
    const value = await db.get(kObjectStoreName, storageKey);
    if (typeof value === 'string') {
      return value;
    }
    // the data used to live in the async storage, move it over and release
    // the local storage space it takes there.
    const legacy = await AsyncStorage.getItem(storageKey);
    if (legacy != null) {
      debug('migrating the inventory data of "%s" to indexeddb', key);
      await db.put(kObjectStoreName, legacy, storageKey);
      await AsyncStorage.removeItem(storageKey);
    }
    return legacy;
  };

  const persist = async (value: T | undefined | null) => {
    const db = await getDatabase();
    if (value == null) {
      await db.delete(kObjectStoreName, storageKey);
    } else {
      await db.put(kObjectStoreName, JSON.stringify(value), storageKey);
    }
  };

  read()
    .then(value => {
      subject$.next(value == null ? null : JSON.parse(value));
    })
    .catch(e => {
      console.error(`error reading the inventory data of "${key}": `, e);
      subject$.next(null);
    })
    .finally(() => {
      subject$
        .pipe(
          concatMap(value =>
            from(
              // a failed write must not tear down the subscription, or nothing
              // gets persisted afterwards.
              persist(value).catch(e => {
                console.error(
                  `error saving the inventory data of "${key}": `,
                  e,
                );
              }),
            ),
          ),
        )
        .subscribe();
    });

  return subject$;
};
